import { useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, ModalLauncher } from "buba-components-ts";
import { History } from "history";

import longArrowRight from "../../assets/icons/other/longArrowRight.svg";

import styles from "./WorkflowActionButton.scss";

type WorkflowActionButtonProps = {
  title: string;
  customStyles?: string;
  type?: "button" | "submit";
  isDisabled?: boolean;
  actionId: string;
  confirmAction: (actionId: string, payload: any) => void;
  description: string;
  customModal?: React.ReactNode;
  isCurrentUserAssignedToInquiry: boolean;
  addMessage: (error: string, type: string) => void;
  completedOrFailed: boolean;
  isRepetable?: boolean;
  onClick?: (actionId: string, history: History<unknown>) => void;
};

const WorkflowActionButton = ({
  title,
  customStyles,
  type = "button",
  isDisabled = false,
  actionId,
  description,
  confirmAction,
  customModal,
  isCurrentUserAssignedToInquiry,
  completedOrFailed,
  isRepetable = false,
  addMessage,
  onClick
}: WorkflowActionButtonProps) => {
  const history = useHistory();
  const [isConfirmButtonClicked, setIsConfirmButtonClicked] = useState(false);
  const checkPermissions = (): boolean => {
    if (!isRepetable && !isCurrentUserAssignedToInquiry && !completedOrFailed) {
      addMessage(
        "Sie sind aktuell nicht berechtigt, diese Aktion durchzuführen. Bitte ordnen Sie sich die Anfrage zu und wiederholen Sie die Aktion.",
        "failure"
      );
      return false;
    }
    return true;
  };

  const handleTriggerClick = (e: React.SyntheticEvent) => {
    // Dont let event bubble if user is not assigned to inquiry, unless it is to assign the inquiry, or the action can't be used because its finished or failed
    if (!checkPermissions()) {
      e.stopPropagation();
    }
  };

  const handleDirectClick = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Check permissions first
    if (!checkPermissions()) {
      return;
    }

    if (onClick) {
      onClick(actionId, history);
    }
  };

  const handleConfirmAction = () => {
    if (isConfirmButtonClicked) return;

    setIsConfirmButtonClicked(true);
    confirmAction(actionId, {});
  };

  const handleModalOpen = () => {
    setIsConfirmButtonClicked(false);
  };

  const disabled = isDisabled || (completedOrFailed && !isRepetable);

  // If onClick is provided, render a simple button instead of modal launcher
  if (onClick) {
    return (
      <Button
        type={type}
        isDisabled={disabled}
        variant="clean"
        size="small"
        iconLeft={longArrowRight}
        customStyles={customStyles}
        onClick={handleDirectClick}
      >
        <span className={disabled ? styles.disabledButton : ""}>{title}</span>
      </Button>
    );
  }

  return (
    <div>
      <ModalLauncher
        isInitiallyOpen={false}
        isDisabled={isDisabled || (completedOrFailed && actionId !== "assign_guarantee_agent")}
        onTriggerClick={handleModalOpen}
        trigger={
          <Button
            type={type}
            isDisabled={disabled}
            variant="clean"
            size="small"
            iconLeft={longArrowRight}
            customStyles={customStyles}
          >
            <span onClick={handleTriggerClick} className={disabled ? styles.disabledButton : ""}>
              {title}
            </span>
          </Button>
        }
        wide
        closeBtnVisible={true}
        customContentStyles={styles.workflowActionConfirmModal}
        header={() => <div className={styles.modalHeader}>{title}</div>}
      >
        <div className={styles.actionContainer}>
          {customModal ? (
            customModal
          ) : (
            <>
              <p className={styles.description}>{description}</p>
              <Button
                customStyles={styles.confirmButton}
                isDisabled={isConfirmButtonClicked}
                onClick={handleConfirmAction}
              >
                Bestätigen
              </Button>
            </>
          )}
        </div>
      </ModalLauncher>
    </div>
  );
};

export default WorkflowActionButton;
